import React, { useContext } from 'react';
import { Tag, Badge } from 'antd';
import { UserOutlined, CalendarOutlined, TrophyOutlined } from '@ant-design/icons';
import { orDash } from '../utils/format';
import GiveawayStatusTag from '../../Giveaways/components/GiveawayStatusTag';
import { FaTicketAlt } from 'react-icons/fa';
import { formatDateStringToDateAndTime, poeValorComPontosEmMil } from '../../../utils/Conversores';
import { IGiveaway } from '../../../interfaces';
import { ThemeContext } from '../../../contexts/ThemeContext';

interface RaffleCardProps {
  raffle: IGiveaway | undefined;
  belongsToThisInvoice?: boolean;
}

export const RaffleCard: React.FC<RaffleCardProps> = ({ raffle, belongsToThisInvoice = true }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  if (!raffle) {
    return <></>;
  }

  const cardBg = isDark ? '#141414' : '#ffffff';
  const itemBg = isDark ? '#1f1f1f' : '#f8fafc';
  const itemBorder = isDark ? '#2a2a2a' : '#eaedf1';
  const labelColor = isDark ? '#8c8c8c' : '#64748b';
  const textColor = isDark ? '#f0f0f0' : '#1e293b';

  return (
    <div
      style={{
        background: cardBg,
        borderRadius: 14,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.35)'
          : '0 2px 10px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          background: isDark ? '#1a1a1a' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isDark ? 'rgba(250, 140, 22, 0.15)' : 'rgba(250, 140, 22, 0.1)',
              color: '#fa8c16',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            <TrophyOutlined />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>
              Sorteio Vinculado
            </div>
            <div style={{ fontSize: 12, color: labelColor }}>
              Sorteio municipal ao qual esta nota concorre
            </div>
          </div>
        </div>

        <Badge
          status={belongsToThisInvoice ? 'success' : 'default'}
          text={
            <span style={{ fontSize: 12, fontWeight: 500, color: belongsToThisInvoice ? '#52c41a' : labelColor }}>
              {belongsToThisInvoice ? 'Esta nota pertence a este sorteio' : 'Não vinculado'}
            </span>
          }
        />
      </div>

      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10,
          }}
        >
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>ID do Sorteio</div>
            <div>
              <Tag color="orange" style={{ fontWeight: 600, margin: 0 }}>
                #{raffle.id}
              </Tag>
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Status do Sorteio</div>
            <div>
              {GiveawayStatusTag({ status: raffle.status, isValid: raffle.is_valid })}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px', gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Descrição / Nome</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: textColor }}>
              {orDash(raffle.description)}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px', gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <CalendarOutlined /> Período de Validade das Notas
            </div>
            <div style={{ fontSize: 12, fontWeight: 500, color: textColor }}>
              {formatDateStringToDateAndTime(raffle.period_beginning)} até {formatDateStringToDateAndTime(raffle.period_ending)}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Limite de Importação</div>
            <div style={{ fontSize: 12, fontWeight: 500, color: textColor }}>
              {formatDateStringToDateAndTime(raffle.import_deadline)}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Data da Realização</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fa8c16' }}>
              {formatDateStringToDateAndTime(raffle.date)}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          <div
            style={{
              background: itemBg,
              borderRadius: 8,
              border: `1px solid ${itemBorder}`,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: isDark ? 'rgba(22, 119, 255, 0.2)' : '#e6f4ff',
                color: '#1677ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              <UserOutlined />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: labelColor }}>Participantes</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: textColor, lineHeight: 1.2 }}>
                {raffle.total_user_participating ? poeValorComPontosEmMil(raffle.total_user_participating) : '--'}
              </div>
            </div>
          </div>

          <div
            style={{
              background: itemBg,
              borderRadius: 8,
              border: `1px solid ${itemBorder}`,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: isDark ? 'rgba(82, 196, 26, 0.2)' : '#f6ffed',
                color: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              <FaTicketAlt />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: labelColor }}>Total de Bilhetes</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: textColor, lineHeight: 1.2 }}>
                {raffle.total_tickets ? poeValorComPontosEmMil(raffle.total_tickets) : '--'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaffleCard;

